import { AlertTriangle, Lock, Unlock, X } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";

// Popup de confirmar bloqueio/desbloqueio (pedido 2026-09-16, "quero igual
// a esse visual") — extraído do PWA (mobile-app.tsx) pra ser reaproveitado
// também no painel desktop (routes/index.tsx) e no atalho Bloquear/
// Desbloquear do popup "Enviar comando" (vehicle-send-command-dialog.tsx),
// que antes mandavam o comando direto (desktop) ou caíam num AlertDialog
// genérico "Deseja realmente enviar este comando?" (atalho), sem essa tela.
// Mostra os dois comandos juntos (não só o que bate com o status atual
// reportado pelo rastreador) — cada botão só aparece se o chamador passar a
// permissão correspondente como true.
export function VehicleBlockConfirmDialog({
  open,
  onOpenChange,
  vehicleName,
  vehiclePlate,
  canLock,
  canUnlock,
  sending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicleName: string;
  vehiclePlate: string;
  canLock: boolean;
  canUnlock: boolean;
  sending?: boolean;
  onConfirm: (action: "block" | "unblock") => void;
}) {
  const handlePick = (action: "block" | "unblock") => {
    onOpenChange(false);
    onConfirm(action);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Fechar"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-muted/70"
        >
          <X className="h-4 w-4" />
        </button>
        <DrawerHeader>
          <DrawerTitle>Bloqueio do veículo</DrawerTitle>
        </DrawerHeader>
        <div className="space-y-4 px-4 pb-4">
          <div className="grid grid-cols-2 gap-y-2 rounded-xl border border-border bg-muted/40 p-4 text-sm">
            <div className="text-muted-foreground">Veículo</div>
            <div className="text-right font-semibold text-foreground">{vehicleName}</div>
            <div className="text-muted-foreground">Placa do veículo</div>
            <div className="text-right font-mono font-semibold text-foreground">{vehiclePlate}</div>
          </div>

          <div className="flex gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              <strong>Atenção!</strong> Isso envia um comando remoto real ao rastreador.
              Certifique-se de que é realmente necessário antes de prosseguir.
            </p>
          </div>

          {canLock && (
            <button
              type="button"
              onClick={() => handlePick("block")}
              disabled={sending}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-destructive py-3.5 text-base font-semibold text-destructive-foreground shadow-sm transition-colors hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Lock className="h-5 w-5" />
              Bloquear
            </button>
          )}

          {canUnlock && (
            <button
              type="button"
              onClick={() => handlePick("unblock")}
              disabled={sending}
              className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-emerald-500 bg-background py-3.5 text-base font-semibold text-emerald-600 shadow-sm transition-colors hover:bg-emerald-500/10 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Unlock className="h-5 w-5" />
              Desbloquear
            </button>
          )}
        </div>
        <DrawerFooter className="pt-0">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="py-1 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            Cancelar
          </button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
